import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './AuthCallback.css';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuthStatus } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const hasProcessed = useRef(false);
  const closeTimer = useRef<number | null>(null);
  
  // Keep track of auth verification
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [verificationError, setVerificationError] = useState<string | null>(null);

  // Effect to handle auto-closing when error is set
  useEffect(() => {
    if (error) {
      closeTimer.current = window.setTimeout(() => {
        try {
          window.close();
          window.setTimeout(() => {
            navigate('/login');
          }, 500);
        } catch (e) {
          console.error('Failed to auto-close tab:', e);
          navigate('/login');
        }
      }, 10000); // Increased to 10 seconds to give more time to see error details
    }
    
    return () => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
    };
  }, [error, navigate]);

  // Handle verification status changes
  useEffect(() => {
    if (verificationStatus === 'error' && verificationError) {
      setError(verificationError);
      setIsLoading(false);
      sessionStorage.removeItem('auth_flow_in_progress');
      // Redirect to auth-failed page after a short delay
      setTimeout(() => {
        navigate('/auth-failed');
      }, 2000);
    } else if (verificationStatus === 'success') {
      // Navigate to homepage on successful verification
      // Record successful authentication time
      sessionStorage.setItem('last_successful_auth_time', Date.now().toString());
      // Clear the auth flow flag
      sessionStorage.removeItem('auth_flow_in_progress');
      console.log('Authentication flow completed successfully');
      
      // Prevent navigation loops by checking if we've already navigated post-auth
      const hasNavigated = sessionStorage.getItem('post_auth_navigated');
      if (!hasNavigated) {
        // Set flag to prevent multiple navigations
        sessionStorage.setItem('post_auth_navigated', 'true');
        navigate('/');
      } else {
        console.log('Skipping post-auth navigation in AuthCallback - already navigated once');
      }
    }
  }, [verificationStatus, verificationError, navigate]);

  useEffect(() => {
    const processCallback = async () => {
      if (hasProcessed.current) {
        return;
      }
      hasProcessed.current = true;
      
      // Set auth flow flag to prevent logout loops
      console.log('[AuthCallback] Setting auth_flow_in_progress flag');
      sessionStorage.setItem('auth_flow_in_progress', 'true');
      
      // First login detection - for better handling of user with no configurations
      if (!localStorage.getItem('kms_first_login')) {
        localStorage.setItem('kms_first_login', 'true');
        console.log('[AuthCallback] First login flag set for configuration handling');
      }
      
      // For debugging, add a timestamp
      localStorage.setItem('auth_callback_timestamp', Date.now().toString());
      
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error_param = params.get('error');
      
      // Check for error parameter in URL
      if (error_param) {
        console.error('OAuth error returned in URL:', error_param);
        setError(`Authentication failed: ${error_param}`);
        setIsLoading(false);
        sessionStorage.removeItem('auth_flow_in_progress');
        return;
      }

      // Handle direct OAuth callback from backend's redirect
      if (location.pathname === '/api/v1/auth/google-callback' || location.pathname === '/auth-success') {
        console.log('On OAuth callback route after backend processing, verifying session...');
        try {
          // Verify the session is established after the backend processed the OAuth flow
          const success = await verifyAuthSession();
          if (!success) {
            sessionStorage.removeItem('auth_flow_in_progress');
          }
        } catch (err: any) {
          console.error('Error verifying authentication session:', err);
          const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
          setError(`Authentication failed: ${errorMessage}`);
          setIsLoading(false);
          sessionStorage.removeItem('auth_flow_in_progress');
        }
        return;
      }

      if (!code && !state && location.pathname === '/auth/callback') {
        // We're on the frontend callback route after OAuth redirect from the backend
        console.log('On frontend callback route after OAuth flow, verifying session...');
        try {
          // Verify the session is established after the backend processed the OAuth flow
          const success = await verifyAuthSession();
          if (!success) {
            sessionStorage.removeItem('auth_flow_in_progress');
          }
        } catch (err: any) {
          console.error('Error verifying authentication session:', err);
          const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
          setError(`Authentication failed: ${errorMessage}`);
          setIsLoading(false);
          sessionStorage.removeItem('auth_flow_in_progress');
        }
        return;
      }

      if (!code) {
        setError('Authentication failed: Missing authorization code from Google.');
        setIsLoading(false);
        sessionStorage.removeItem('auth_flow_in_progress');
        return;
      }

      try {
        console.log('Processing OAuth callback with code and state...');
        
        // After backend processes the code, verify that session cookies are properly established
        const success = await verifyAuthSession();
        if (!success) {
          sessionStorage.removeItem('auth_flow_in_progress');
        }
      } catch (err: any) {
        console.error('Error during authentication callback:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(`Authentication failed: ${errorMessage}`);
        setIsLoading(false);
        sessionStorage.removeItem('auth_flow_in_progress');
      }
    };
    


    // Function to verify that the authentication session is valid
    const verifyAuthSession = async (retryCount = 0, maxRetries = 2): Promise<boolean> => {
      console.log(`Verifying auth session (attempt ${retryCount + 1}/${maxRetries})...`);
      
      // Check cookies at the start of the verification
      console.log('Cookie debug info:', { cookies: document.cookie });
      
      try {
        // Use proper cache control headers instead of timestamp
        const response = await fetch(`/api/v1/auth/status`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (!response.ok) {
          console.error(`Auth status check failed: ${response.status}`);
          return false;
        }
        
        const data = await response.json();
        console.log('Auth status response:', data);
        
        if (data.authenticated) {
          // Authentication is successful, save user data
          if (data.user) {
            try {
              localStorage.setItem('kms_auth_user', JSON.stringify(data.user));
              localStorage.setItem('kms_user_email', JSON.stringify(data.user.email));
              localStorage.setItem('kms_auth_check', 'true');
              
              
              // Authentication succeeded
              setVerificationStatus('success');
              return true;
            } catch (storageError) {
              console.error('Error storing auth data:', storageError);
            }
          }
          
          // If we got here, authentication succeeded
          setVerificationStatus('success');
          return true;
        } else {
          console.log('Auth check returned false, session not verified yet');
        }
      } catch (error) {
        console.error('Error during auth verification:', error);
      }
      
      // If we haven't verified auth yet and still have retries left
      if (retryCount < maxRetries) {
        console.log(`Authentication not verified yet, retrying (${retryCount + 1}/${maxRetries})...`);
        // Wait for a short time and retry
        await new Promise(resolve => setTimeout(resolve, 1000));
        return verifyAuthSession(retryCount + 1, maxRetries);
      } else {
        console.error('Failed to verify authentication after max retries');
        setVerificationStatus('error');
        setVerificationError('Could not verify your authentication after multiple attempts. Please try logging in again.');
        // Clear navigation flag to allow future attempts
        sessionStorage.removeItem('post_auth_navigated');
        // Redirect to recovery page instead of login
        navigate('/auth-failed');
        return false;
      }
    };

    processCallback();
  }, [navigate, checkAuthStatus, location.search, location.pathname]);

  if (verificationStatus === 'error' && verificationError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
          <div className="text-red-500 text-center mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 dark:text-white mb-4">Authentication Failed</h2>
          <p className="text-gray-600 dark:text-gray-300 text-center mb-6">{verificationError}</p>
          
          <div className="flex flex-col items-center">
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mb-2"
            >
              Return to Login
            </button>
            <p className="text-sm text-gray-500 mt-2">This window will redirect to login shortly...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-gray-700 dark:text-gray-300">Completing authentication...</p>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Establishing secure session</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
          <div className="text-red-500 text-center mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 dark:text-white mb-4">Authentication Failed</h2>
          <p className="text-gray-600 dark:text-gray-300 text-center mb-6">{error}</p>
          

          
          <div className="flex flex-col items-center">
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mb-2"
            >
              Return to Login
            </button>
            <p className="text-sm text-gray-500 mt-2">This tab will automatically close in 10 seconds...</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback; 